/** Recharts wrappers used for lab trends and growth curves. */
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
  ReferenceLine,
} from 'recharts';

export interface TrendPoint {
  date: string;
  value: number;
  referenceLow?: number | null;
  referenceHigh?: number | null;
}

/**
 * A line chart with an optional reference-range band (low..high). Scaling is
 * honest: the Y domain auto-fits the data and the reference range without
 * forcing a zero baseline that would distort the trend.
 */
export function TrendChart({
  data,
  unit,
  color = '#2563eb',
  referenceLow,
  referenceHigh,
  height = 220,
}: {
  data: TrendPoint[];
  unit?: string;
  color?: string;
  referenceLow?: number | null;
  referenceHigh?: number | null;
  height?: number;
}) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
        <YAxis
          tick={{ fontSize: 11 }}
          domain={['auto', 'auto']}
          width={48}
          label={unit ? { value: unit, angle: -90, position: 'insideLeft', fontSize: 11 } : undefined}
        />
        <Tooltip
          formatter={(v: number) => [`${v}${unit ? ` ${unit}` : ''}`, '']}
          labelStyle={{ fontSize: 12 }}
          contentStyle={{ fontSize: 12 }}
        />
        {referenceLow != null && referenceHigh != null && (
          <ReferenceArea
            y1={referenceLow}
            y2={referenceHigh}
            fill="#22c55e"
            fillOpacity={0.08}
            ifOverflow="extendDomain"
          />
        )}
        {referenceLow != null && (
          <ReferenceLine y={referenceLow} stroke="#16a34a" strokeDasharray="4 2" ifOverflow="extendDomain" />
        )}
        {referenceHigh != null && (
          <ReferenceLine y={referenceHigh} stroke="#16a34a" strokeDasharray="4 2" ifOverflow="extendDomain" />
        )}
        <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}
